<template>
  <div class="pa-form-group" role="checkbox" :aria-checked="shouldBeChecked">
    <input
      :id="value"
      type="checkbox"
      :value="value"
      :disabled="disabled"
      :checked="shouldBeChecked"
      :aria-checked="shouldBeChecked"
      :class="computedClass"
      @change="updateInput"
    />
    <label :for="value">{{ label }}</label>
  </div>
</template>

<script>
import theme from '@/mixins/theme';

export default {
  name: 'Checkbox',
  mixins: [theme],
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    value: {
      type: String,
    },
    /**
     * @model
     * */
    modelValue: {
      default: '',
      type: [String, Array],
    },
    label: {
      required: true,
      type: String,
    },
    checked: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    trueValue: {
      default: true,
      type: Boolean,
    },
    falseValue: {
      default: false,
      type: Boolean,
    },
    /**
     * The type of the checkbox
     * @values primary, secondary, success, danger
     * @default is primary
     * */
    type: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'secondary', 'success', 'danger'].indexOf(value) !== -1,
    },
    /**
     * The size of the checkbox
     * @values small, medium, large
     * @default is medium
     * */
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].indexOf(value) !== -1,
    },
  },
  computed: {
    /**
     * This checks if the v-model whether an array or string includes/contains/is the value,
     * to update the check status
     * */
    shouldBeChecked() {
      if (this.modelValue instanceof Array) return this.modelValue.includes(this.value);

      return this.modelValue === this.trueValue;
    },
    computedClass() {
      return `pa-${this.type} pa-${this.size}`;
    },
  },
  methods: {
    /**
     * Triggers when there is interaction with the checkbox
     * Checks if the v-model is of type array or string in this case
     * If is is of type array, and the checked state of the checkbox is true,
     * the value prop of the checkbox ix pushed into the model to update the options,
     * if it is false, the value is spliced out of the model
     * If the v-model is of type string or any other type, the model is updated and
     * the value of the checked is updated accordingly
     * */
    updateInput(event) {
      const isChecked = event.target.checked;

      if (this.modelValue instanceof Array) {
        const newValue = [...this.modelValue];

        if (isChecked) {
          newValue.push(this.value);
        } else {
          newValue.splice(newValue.indexOf(this.value), 1);
        }

        /**
         * @event change
         * @param {boolean || string} newValue bound to the checkbox
         * */
        this.$emit('change', newValue);
      } else {
        /**
         * @event change
         * @param {boolean} isChecked new value of prop value after change
         * updateInput has been called
         * */
        this.$emit('change', isChecked ? this.trueValue : this.falseValue);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import './checkbox';
</style>
